
import { Suspense, useCallback, useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthContext';
import { Outlet, useLocation } from 'react-router-dom';
import { SettingsSurface } from './settings/SettingsSurface';
import { SettingsLegal } from './settings/SettingsRow';
import { RouteFallback } from '@/components/layout/RouteFallback';
import { Sidebar } from '@/components/layout/Sidebar';
import { PageHeaderBar } from '@/components/layout/PageHeaderBar';
import { PageHeaderSlotsProvider } from '@/components/layout/PageHeaderSlots';
import { BottomTabBar } from '@/components/layout/BottomTabBar';
import { HelpSurface } from '@/components/help/HelpSurface';
import { HELP_INDEX, toHelpIntent, type HelpIntent } from '@/components/help/helpIntent';
import { TutorialPeekTab } from '@/components/help/TutorialPeekTab';
import { UpgradeModalProvider } from '@/components/tier/UpgradeModal';
import { CommandPalette } from '@/components/command/CommandPalette';
import { CommandPaletteProvider } from '@/components/command/useCommandPalette';
import { FirstRunProvider } from '@/components/first-run/FirstRunProvider';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { readSidebarCollapsed, writeSidebarCollapsed } from '@/lib/sidebarCollapse';
import styles from './layout.module.css';

export default function AuthenticatedLayout() {
  const { user } = useAuth();
  usePageTitle();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const onAriaChat = pathname === '/aria' || pathname.startsWith('/aria/');
  // Every settings route uses the same open page surface and legal footer.
  const isSettingsSubPage = pathname === '/settings' || pathname.startsWith('/settings/');
  // The invoice builder (/invoices/:id) is a golden-split workspace like Aria:
  // PageHeaderBar stays, main goes flush, the page's two panes own scrolling.
  // (The old /queue/generate-invoice full-bleed editor is retired — its route
  // is a create-and-redirect shim now.)
  const onInvoiceBuilder = /^\/invoices\/[^/]+$/.test(pathname);
  // New-IA bottom tab bar (mobile only — not even mounted on desktop so its
  // data hooks don't fetch there). Hidden inside an open thread, where the
  // composer must own the bottom edge. `/(messages|conversations)/team` is the
  // team-chat INDEX — a list screen, so it keeps the bar; only its
  // `/team/:chatId` children are threads (pinned by the contracts source scan
  // in team-chat-and-meetings.test.ts).
  const onThreadDetail = /^\/(messages|conversations)\/(?!team\/?$)./.test(pathname);
  const showTabBar = isMobile && !onThreadDetail;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // What the Help drawer opens onto. Every open without an intent (the header
  // and org-menu Help buttons, the palette's Help action, Home's "All steps")
  // resets it to the index; the tutorial peek tab asks for its video.
  const [helpIntent, setHelpIntent] = useState<HelpIntent>(HELP_INDEX);
  const requestHelp = useCallback((intent?: HelpIntent) => {
    setHelpIntent(toHelpIntent(intent));
    setHelpOpen(true);
  }, []);
  const openHelpIndex = useCallback(() => requestHelp(), [requestHelp]);
  // The support toast reopens Help straight into the messenger — no intent.
  const showHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  // Desktop rail collapse. Lives here because two siblings need it: the rail
  // slides out, the content surface reclaims its 268px, and PageHeaderBar grows
  // the button that brings it back. Persisted per-browser.
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      writeSidebarCollapsed(!prev);
      return !prev;
    });
  }, []);

  // Navigating from inside the drawer (nav link, conversation row) closes it.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [pathname]);

  // Page scroll behind the open drawer is locked by the drawer's own modal hook
  // (Sidebar's useModalA11y), on the shared counter: a second lock here would
  // drop the command palette's when a shortcut opens it over the drawer.

  const contentCardClass = [
    styles.contentCard,
    onAriaChat && styles.contentCardAria,
    onInvoiceBuilder && styles.contentCardBuilder,
  ]
    .filter(Boolean)
    .join(' ');

  const mainClass = [
    styles.main,
    onAriaChat && styles.mainAria,
    onInvoiceBuilder && styles.mainBuilder,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <UpgradeModalProvider>
      {/* First-run state (Get set up, the sample call, the coach marks, Help's
          Replay the tour) lives above the shell so the Help drawer and the
          routed page share one instance. Nothing in it fires on load; Reset
          tutorial progress reopens Help at its index (Get set up on top). */}
      <FirstRunProvider onReset={openHelpIndex}>
      {/* One palette instance for the whole shell: the provider owns the ⌘K / `/`
          shortcuts and the open state the sidebar's two pills drive. It sits
          inside UpgradeModalProvider because the shell below it opens the
          upgrade modal (the sidebar's Add organization). */}
      <CommandPaletteProvider onHelp={requestHelp}>
        {/* Header slots sit inside the palette provider so both the bar and the
            routed page (which registers slots) share one provider instance. */}
        <PageHeaderSlotsProvider>
          <div
            className={[
              styles.root,
              showTabBar ? styles.rootWithTabbar : '',
              collapsed ? styles.rootCollapsed : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Sidebar
              isMobileOpen={isMobileOpen}
              onClose={() => setIsMobileOpen(false)}
              onHelpClick={openHelpIndex}
              collapsed={collapsed}
              onToggleCollapse={toggleCollapsed}
            />
            <div className={styles.body}>
              {/* Staff session warning. Sticky rather than a top banner: the
                  owner must be able to tell at any scroll position that the
                  session open on their account is not their own. */}
              {user?.impersonated && (
                <div className={styles.impersonationBar} role="status">
                  <ShieldAlert size={15} aria-hidden="true" />
                  <span>A Ten Four teammate is signed in to your account with admin access.</span>
                </div>
              )}
              <div className={contentCardClass}>
                <PageHeaderBar
                  onMenuClick={() => setIsMobileOpen(true)}
                  onHelpClick={openHelpIndex}
                  sidebarCollapsed={collapsed}
                  onExpandSidebar={toggleCollapsed}
                />
                <main className={mainClass}>
                  {/* PERF-13: page chunks load inside the shell — sidebar/header
                      persist during a lazy-route load instead of a blank screen. */}
                  <Suspense fallback={<RouteFallback />}>
                    {isSettingsSubPage ? <SettingsSurface key={pathname}><Outlet /></SettingsSurface> : <Outlet />}
                  </Suspense>
                  {isSettingsSubPage && <SettingsLegal />}
                </main>
              </div>
            </div>
            {showTabBar && <BottomTabBar />}
            {/* The per-page explanation-video tab (design system
                #help-peek-tab). Inside .root so it inherits --mobile-tabbar-h
                and clears the tab bar; it stands down while Help or the phone
                nav drawer is open. */}
            <TutorialPeekTab suppressed={helpOpen || isMobileOpen} />
          </div>
          <CommandPalette />
          {/* Inside CommandPaletteProvider: the drawer's Shortcuts item opens
              the palette. Owns the drawer ⇄ messenger swap + the reply toast. */}
          <HelpSurface
            open={helpOpen}
            intent={helpIntent}
            onOpen={showHelp}
            onClose={closeHelp}
          />
        </PageHeaderSlotsProvider>
      </CommandPaletteProvider>
      </FirstRunProvider>
    </UpgradeModalProvider>
  );
}
